'use es6';

import PropTypes from 'prop-types';
import { PureComponent } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { connect } from 'react-redux';
import { isEqual, isEmpty, findWhere, contains } from 'underscore';
import { Link } from 'foundations-components/transitional/components/Link';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Small } from 'foundations-components/transitional/components/Small';
import { View } from 'foundations-components/components/View';
import { FormFieldConfigHeader } from 'FormsUI/components/editor/form/configuration/FormFieldConfigHeader';
import ConfigurationPanelHeader from './ConfigurationPanelHeader';
import CommunicationConsentCheckbox from './CommunicationConsentCheckbox';
import LawfulBasisSelect from 'ui-gdpr-components/components/LawfulBasisSelect';
import SubscriptionTypeSelect from 'FormsUI/components/common/SubscriptionTypeSelect';
import { FormControl } from 'foundations-components/transitional/components/FormControl';
import { CommentableArea } from 'collaboration-sidebar';
import { getLegalConsentOptions, getCurrentSelectedLegalConsentOption, getUnselectedSubscriptionDefinitions, getSubscriptionDefinitions } from 'FormsUI/selectors/LegalConsent';
import { getSelectedSubscriptionIds, getUnavailableEmailSubscriptionsIdsByFormBusinessUnit } from 'FormsUI/selectors/LegalConsentPanel';
import { updateLegalConsentProperty as updateLegalConsentPropertyAction, getSubscriptionDefinitions as getSubscriptionDefinitionsFromAction, removeLegalConsentCheckbox as removeLegalConsentCheckboxAction } from 'FormsUI/actions/LegalConsent';
import updateLegalConsentCheckboxAction from 'FormsUI/actions/LegalConsent/updateLegalConsentCheckbox';
import { addValidationErrorIfNeeded, removeValidationErrorIfNeeded } from 'FormsUI/actions/FormDefinition';
import { ValidationKeys, ValidationType } from 'FormsUI/constants/FormFieldValidations';
import { EditableLegalConsentKeys } from 'FormsCommonComponents/constants/LegalConsent';
import LegalConsent, { getEditableLegalConsentKeysForLegalConsentType } from 'FormsCommonComponents/lib/LegalConsent';
import { getEmailSubscriptionTypesSettingsRoute, getLegalConsentSettingsRoute } from 'FormsUI/lib/RouteGenerators';
import { isUngatedForSubscriptionGroups } from 'FormsUI/selectors/Gates';
import RichTextEditor from 'FormsUI/loadable/AsyncRichTextEditor';
import AddConsentCheckboxButton from 'FormsUI/components/editor/form/configuration/legalConsentConfigurationPanel/AddConsentCheckboxButton';
import { getLegalConsentErrors, getLegalConsentWarnings } from 'FormsUI/utils/LegalConsentPanelUtils';
import { ConfigPanelWrapper } from 'FormsUI/components/editor/form/configuration/ConfigPanelWrapper';
import { View as FoundationsView } from 'foundations-components/transitional/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const i18nKey = suffix => `formsLegalText.configurationPanel.${suffix}`;
export class LegalConsentConfigurationPanel extends PureComponent {
  constructor(props) {
    super(props);
    const {
      legalConsent,
      currentLegalConsentType
    } = props;
    const validations = getLegalConsentErrors({
      legalConsent,
      currentLegalConsentType
    });
    const warnings = getLegalConsentWarnings({
      legalConsent,
      currentLegalConsentType
    });
    this.state = {
      warnings,
      validations
    };
    this.handlePropertyUpdate = this.handlePropertyUpdate.bind(this);
    this.handleCheckboxUpdate = this.handleCheckboxUpdate.bind(this);
    this.handleDeleteCheckbox = this.handleDeleteCheckbox.bind(this);
    this.validateProperty = this.validateProperty.bind(this);
    this.getErrorProperties = this.getErrorProperties.bind(this);
  }
  componentDidMount() {
    this.props.getDefinitions();
  }
  updateValidation(newValidations) {
    const {
      addValidationError,
      removeValidationError
    } = this.props;
    this.setState({
      validations: newValidations
    });
    if (isEmpty(newValidations)) {
      removeValidationError('form', ValidationKeys.LEGAL_CONSENT, ValidationType.INVALID);
    } else {
      addValidationError('form', ValidationKeys.LEGAL_CONSENT, ValidationType.INVALID);
    }
  }
  validateProperty() {
    const {
      legalConsent,
      currentLegalConsentType
    } = this.props;
    const validation = getLegalConsentErrors({
      legalConsent,
      currentLegalConsentType
    });
    this.updateValidation(validation);
    const warnings = getLegalConsentWarnings({
      legalConsent,
      currentLegalConsentType
    });
    this.setState({
      warnings
    });
  }
  handlePropertyUpdate(key, value) {
    const {
      updateLegalConsentProperty,
      legalConsent
    } = this.props;
    updateLegalConsentProperty(legalConsent, key, value);
  }
  handleCheckboxUpdate(checkbox, oldCheckbox) {
    const {
      updateLegalConsentCheckbox
    } = this.props;
    updateLegalConsentCheckbox(checkbox, oldCheckbox);
  }
  handleDeleteCheckbox(checkbox) {
    const {
      removeLegalConsentCheckbox,
      legalConsent
    } = this.props;
    removeLegalConsentCheckbox(legalConsent, checkbox);
  }
  shouldShowProperty(key) {
    const {
      currentLegalConsentType
    } = this.props;
    return contains(getEditableLegalConsentKeysForLegalConsentType(currentLegalConsentType), key);
  }
  renderCommunicationConsentCheckboxes(checkboxes = []) {
    const {
      availableSubscriptionDefinitions,
      allSubscriptionDefinitions,
      ungatedForSubscriptionGroups,
      selectedSubscriptionIds,
      subscriptionIdsToHide
    } = this.props;
    const {
      validations
    } = this.state;
    const length = checkboxes.length;
    let addButtonLink = /*#__PURE__*/_jsx(AddConsentCheckboxButton, {
      onClick: () => this.handleCheckboxUpdate(),
      disabled: isEmpty(availableSubscriptionDefinitions)
    });
    if (isEmpty(availableSubscriptionDefinitions)) {
      const settingsLink = /*#__PURE__*/_jsx(Link, {
        href: getEmailSubscriptionTypesSettingsRoute(),
        external: true,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'formsLegalText.configurationPanel.settingsLink'
        })
      });
      addButtonLink = /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedReactMessage, {
          message: 'formsLegalText.configurationPanel.disabledCheckbox',
          options: {
            settingsLink
          }
        }),
        children: addButtonLink
      });
    }
    if (length === 0) {
      return addButtonLink;
    }
    return checkboxes.map((checkbox, index) => {
      const maybeRenderAddButton = length === index + 1 ? addButtonLink : null;
      const currentDefinition = findWhere(allSubscriptionDefinitions, {
        id: checkbox.communicationTypeId
      });
      const uniqueKey = `${EditableLegalConsentKeys.COMMUNICATION_CONSENT_CHECKBOXES}.${checkbox.communicationTypeId}`;
      let errorProps;
      if (!isEmpty(validations[uniqueKey])) {
        errorProps = {
          error: true,
          validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`validationMessages.${validations[uniqueKey]}`)
          })
        };
      }
      return /*#__PURE__*/_jsxs(FoundationsView, {
        marginBlockEnd: "500",
        as: "div",
        children: [/*#__PURE__*/_jsx(CommunicationConsentCheckbox, {
          index: index,
          isDeletable: index > 0,
          consentCheckbox: checkbox,
          subscriptionDefinition: currentDefinition,
          errorProps: errorProps,
          availableSubscriptionDefinitions: availableSubscriptionDefinitions,
          selectedSubscriptionIds: selectedSubscriptionIds,
          subscriptionIdsToHide: subscriptionIdsToHide,
          onChange: updatedCheckbox => this.handleCheckboxUpdate(updatedCheckbox, checkbox),
          onBlur: latestValue => this.validateProperty(uniqueKey, latestValue),
          onDelete: () => this.handleDeleteCheckbox(checkbox),
          ungatedForSubscriptionGroups: ungatedForSubscriptionGroups
        }), maybeRenderAddButton]
      }, uniqueKey);
    });
  }
  renderSubscriptionTypeSelect() {
    const {
      legalConsent,
      ungatedForSubscriptionGroups
    } = this.props;
    const value = legalConsent.get(EditableLegalConsentKeys.SUBSCRIPTION_TYPES).filter(id => !!id);
    return /*#__PURE__*/_jsx(SubscriptionTypeSelect, {
      onChange: subArray => {
        const ids = subArray.map(({
          id
        } = {}) => id).filter(id => !!id);
        this.handlePropertyUpdate(EditableLegalConsentKeys.SUBSCRIPTION_TYPES, ids);
      },
      formControlProps: {
        label: null // Provide our own label
      },
      value: value,
      buttonUse: "form-on-dark",
      multi: true,
      isUngatedForSubscriptionGroups: ungatedForSubscriptionGroups
    });
  }
  renderLawfulBasisSelect() {
    const {
      legalConsent
    } = this.props;
    return /*#__PURE__*/_jsx(LawfulBasisSelect, {
      hideConsent: true,
      hideContract: true,
      hideNonGdpr: true,
      buttonUse: "form-on-dark",
      formControlProps: {
        label: null // Provide our own label
      },
      value: legalConsent.get(EditableLegalConsentKeys.LAWFUL_BASIS),
      onChange: ({
        target: {
          value
        }
      }) => this.handlePropertyUpdate(EditableLegalConsentKeys.LAWFUL_BASIS, value)
    });
  }
  renderSettingsLink() {
    const settingsLink = /*#__PURE__*/_jsx(Link, {
      href: getLegalConsentSettingsRoute(),
      external: true,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: 'formsLegalText.configurationPanel.settingsLink'
      })
    });
    return /*#__PURE__*/_jsx(FormattedReactMessage, {
      message: 'formsLegalText.configurationPanel.editSettingsLink',
      options: {
        settingsLink
      }
    });
  }
  renderProcessingConsentCheckboxLabel(checkbox, key) {
    const {
      validations
    } = this.state;
    let errorProps;
    if (!isEmpty(validations[key])) {
      errorProps = {
        error: true,
        validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`validationMessages.${validations[key]}`)
        })
      };
    }
    return /*#__PURE__*/_jsx(RichTextEditor, {
      content: checkbox,
      onChange: value => {
        this.handlePropertyUpdate(key, value);
      },
      onBlur: latestValue => {
        this.validateProperty(key, latestValue);
      },
      "data-test-id": "rich-text-small",
      errorProps: errorProps,
      isSingleLine: true,
      config: "GDPR"
    });
  }
  getErrorProperties(key) {
    const {
      validations,
      warnings
    } = this.state;
    if (!isEmpty(validations[key])) {
      return {
        error: true,
        validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`validationMessages.${validations[key]}`)
        })
      };
    }
    if (!isEmpty(warnings[key])) {
      return {
        error: false,
        validationMessage: /*#__PURE__*/_jsx(FormattedMessage, {
          message: i18nKey(`warningMessages.${warnings[key]}`)
        })
      };
    }
    return {};
  }
  renderEditableProperties() {
    const {
      legalConsent,
      currentLegalConsentType
    } = this.props;
    return Object.values(getEditableLegalConsentKeysForLegalConsentType(currentLegalConsentType) || {}).map(key => {
      if (!this.shouldShowProperty(key)) {
        return null;
      }
      let renderedProperty;
      const currentValue = legalConsent.get(key);
      if (isEqual(key, EditableLegalConsentKeys.COMMUNICATION_CONSENT_CHECKBOXES)) {
        renderedProperty = this.renderCommunicationConsentCheckboxes(legalConsent.get(key));
      } else {
        let innerProperty;
        if (isEqual(key, EditableLegalConsentKeys.SUBSCRIPTION_TYPES)) {
          innerProperty = this.renderSubscriptionTypeSelect();
        } else if (isEqual(key, EditableLegalConsentKeys.LAWFUL_BASIS)) {
          innerProperty = this.renderLawfulBasisSelect();
        } else if (isEqual(key, EditableLegalConsentKeys.PROCESSING_CONSENT_CHECKBOX_LABEL)) {
          innerProperty = this.renderProcessingConsentCheckboxLabel(currentValue, key);
        } else {
          innerProperty = /*#__PURE__*/_jsxs(_Fragment, {
            children: [isEqual(key, EditableLegalConsentKeys.COMMUNICATION_CONSENT_TEXT) && /*#__PURE__*/_jsx(Small, {
              use: "help",
              children: /*#__PURE__*/_jsx(FormattedMessage, {
                message: i18nKey(`properties.${key}.subtext`)
              })
            }), /*#__PURE__*/_jsx(RichTextEditor, {
              config: "GDPR",
              content: currentValue,
              onChange: value => this.handlePropertyUpdate(key, value),
              onBlur: updatedValue => {
                this.validateProperty(key, updatedValue);
              }
            })]
          });
        }
        renderedProperty = /*#__PURE__*/_jsx(FormControl, Object.assign({
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: i18nKey(`properties.${key}.name`)
          })
        }, this.getErrorProperties(key), {
          children: innerProperty
        }));
      }
      return /*#__PURE__*/_jsx(CommentableArea, {
        moduleId: `legal-consent-config-panel-${key}`,
        description: {
          translationKey: `formsLegalConsentConfig.${key}`
        },
        children: /*#__PURE__*/_jsx("div", {
          "data-test-id": "config-input-field",
          children: renderedProperty
        }, key)
      }, key);
    });
  }
  render() {
    return /*#__PURE__*/_jsxs(ConfigPanelWrapper, {
      "data-commenting-scroll-container": true,
      children: [/*#__PURE__*/_jsx(ConfigurationPanelHeader, {
        handleDone: this.props.hideConfigurationPanel,
        customLink: this.renderSettingsLink()
      }), /*#__PURE__*/_jsx(View, {
        paddingBlockStart: "1200",
        children: /*#__PURE__*/_jsx(FormFieldConfigHeader, {
          label: /*#__PURE__*/_jsx(FormattedMessage, {
            message: 'formsLegalText.configurationPanel.header'
          })
        })
      }), /*#__PURE__*/_jsx(View, {
        padding: "600",
        children: this.renderEditableProperties()
      })]
    });
  }
}
LegalConsentConfigurationPanel.propTypes = {
  legalConsent: PropTypes.instanceOf(LegalConsent).isRequired,
  selectedSubscriptionIds: PropTypes.array,
  currentLegalConsentType: PropTypes.string.isRequired,
  addValidationError: PropTypes.func.isRequired,
  removeValidationError: PropTypes.func.isRequired,
  allSubscriptionDefinitions: PropTypes.array,
  availableSubscriptionDefinitions: PropTypes.array,
  subscriptionIdsToHide: PropTypes.array,
  getDefinitions: PropTypes.func.isRequired,
  hideConfigurationPanel: PropTypes.func.isRequired,
  ungatedForSubscriptionGroups: PropTypes.bool.isRequired,
  updateLegalConsent: PropTypes.func.isRequired,
  updateLegalConsentCheckbox: PropTypes.func.isRequired,
  removeLegalConsentCheckbox: PropTypes.func.isRequired,
  updateLegalConsentProperty: PropTypes.func.isRequired
};
const mapStateToProps = state => ({
  selectedSubscriptionIds: getSelectedSubscriptionIds(state),
  legalConsent: getLegalConsentOptions(state),
  currentLegalConsentType: getCurrentSelectedLegalConsentOption(state),
  availableSubscriptionDefinitions: getUnselectedSubscriptionDefinitions(state),
  allSubscriptionDefinitions: getSubscriptionDefinitions(state),
  ungatedForSubscriptionGroups: isUngatedForSubscriptionGroups(state),
  subscriptionIdsToHide: getUnavailableEmailSubscriptionsIdsByFormBusinessUnit(state)
});
const actionCreators = {
  updateLegalConsentProperty: updateLegalConsentPropertyAction,
  updateLegalConsentCheckbox: updateLegalConsentCheckboxAction,
  getDefinitions: getSubscriptionDefinitionsFromAction,
  addValidationError: addValidationErrorIfNeeded,
  removeValidationError: removeValidationErrorIfNeeded,
  removeLegalConsentCheckbox: removeLegalConsentCheckboxAction
};
export default connect(mapStateToProps, actionCreators)(LegalConsentConfigurationPanel);